import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { CustomUser } from 'api/models/CustomUser';
import { ApiService } from 'api/services/ApiService';

const getUserLabel = (user: CustomUser) => {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || user.email || user.username || '';
};

type StaffUserSelectProps = {
  label: string;
  value: CustomUser | null;
  onChange: (user: CustomUser | null) => void;
  disabled?: boolean;
  testId?: string;
  staffUsers?: CustomUser[];
  isLoadingStaff?: boolean;
  autoFocus?: boolean;
};

const StaffUserSelect = ({
  label,
  value,
  onChange,
  disabled = false,
  testId,
  staffUsers: staffUsersProp,
  isLoadingStaff,
  autoFocus = false,
}: StaffUserSelectProps) => {
  const { data: fetchedStaffUsers = [], isLoading: isFetchingStaff } = useQuery({
    queryKey: ['users-list', 'staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList();
      return (response.filter(user => !user.email?.includes('+')) as CustomUser[]) || [];
    },
    staleTime: 60 * 60 * 1000,
    enabled: staffUsersProp == null,
  });

  const staffUsers = staffUsersProp ?? fetchedStaffUsers;
  const isLoading = isLoadingStaff ?? isFetchingStaff;

  return (
    <Autocomplete
      options={staffUsers}
      value={value}
      onChange={(_, user) => onChange(user)}
      getOptionLabel={getUserLabel}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      loading={isLoading}
      disabled={disabled}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          size="small"
          autoFocus={autoFocus}
          inputProps={{ ...params.inputProps, 'data-testid': testId }}
        />
      )}
    />
  );
};

export default StaffUserSelect;
